"use client";

import { ShieldCheck, Lock, Zap, Server } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Vpntechnicalsetup() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Server,
    number: "one",
    title: "Global Server Grid",
    description: "We deploy high-speed nodes across 100+ regions, ensuring your data travels through optimized paths with zero congestion."
  }, {
    icon: Lock,
    number: "two",
    title: "Military-Grade Tunnel",
    description: "Our protocol uses AES-256 encryption to wrap your traffic, making it invisible to ISPs, hackers, and surveillance."
  }, {
    icon: ShieldCheck,
    number: "three",
    title: "Zero-Log Architecture",
    description: "We operate on RAM-only servers that wipe all session data instantly upon disconnect, ensuring total digital privacy."
  }, {
    icon: Zap,
    number: "four",
    title: "Instant Handshake",
    description: "Optimized connection logic reduces latency to near-zero, providing a seamless experience for streaming and gaming."
  }];
  const stats = [{
    number: "10",
    unit: "Gbps",
    label: "Network speed"
  }, {
    number: "0",
    unit: "",
    label: "Data logs kept"
  }, {
    number: "24",
    unit: "/7",
    label: "Active protection"
  }];
  return <section data-section-id="153" id="vpntechnicalsetup" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[60%]" style={{
      background: "radial-gradient(ellipse 60% 50% at 50% 0%, hsla(22, 100%, 50%, 0.12), transparent 70%)"
    }} />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(18)].map((_, i) => <span key={i} data-index={i} className="absolute rounded-lg" style={{
        width: `${2 + i % 3}px`,
        height: `${2 + i % 3}px`,
        backgroundColor: "hsla(221, 15%, 40%, 0.25)",
        top: `${i * 53 % 100}%`,
        left: `${i * 37 % 100}%`,
        animation: `floatDust ${12 + i % 8}s ease-in-out ${i * 0.4}s infinite alternate`,
        filter: "blur(0.5px)"
      }} />)}
        <style>{`
          @keyframes floatDust {
            0%   { transform: translate(0, 0); opacity: 0.2; }
            50%  { transform: translate(20px, -30px); opacity: 0.6; }
            100% { transform: translate(-15px, -60px); opacity: 0.1; }
          }
          @keyframes fadeUpStep {
            0%   { opacity: 0; transform: translateY(24px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[45fr_55fr] lg:gap-20">
          {}
          <div ref={leftRef} className={`lg:sticky lg:top-24 lg:self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-4">
              <span aria-hidden="true" className="block h-px w-12" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Technology
              </span>
            </div>

            <h2 className="mt-8 font-sans font-bold text-foreground leading-[1.05] tracking-tight" style={{
            fontSize: "clamp(2.75rem, 5.5vw, 4.5rem)"
          }}>
              Advanced <em className="italic font-normal">encryption.</em> Total digital freedom.
            </h2>

            <p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-muted-foreground font-sans">
              No compromises. No tracking. Just high-performance tunneling built for the modern web. 
              Our infrastructure ensures your identity remains private while you browse at maximum speed.
            </p>

            <div className="mt-10">
              <Link to="https://vpngenix.com/go/NordVPN" newTab>
                <Button variant="outline" size="lg" className="rounded-lg border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground px-6">
                  Explore our tech
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="flex flex-col">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className={`group flex items-start gap-6 md:gap-8 py-8 md:py-10 ${i !== 0 ? "border-t" : ""}`} style={{
              borderColor: "var(--border)",
              animation: `fadeUpStep 700ms ease-out ${i * 120}ms both`
            }}>
                  <div className="shrink-0">
                    <Icon className="h-9 w-9 md:h-10 md:w-10" strokeWidth={1.25} style={{
                  color: "var(--primary)"
                }} />
                  </div>

                  <div className="flex-1">
                    <div className="font-sans font-semibold text-base md:text-lg mb-2" style={{
                  color: "var(--primary)"
                }}>
                      {step.number}
                    </div>
                    <h3 className="font-sans font-bold text-2xl md:text-[28px] leading-tight text-foreground">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground font-sans max-w-lg">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 border-t pt-12 md:pt-16" style={{
        borderColor: "var(--border)"
      }}>
          {stats.map((stat, i) => <div key={i} data-index={i} className={`flex flex-col items-center md:items-start px-4 md:px-10 py-6 md:py-0 ${i !== 0 ? "md:border-l" : ""} ${i !== stats.length - 1 ? "border-b md:border-b-0" : ""}`} style={{
          borderColor: "var(--border)"
        }}>
              <div className="flex items-baseline gap-2">
                <span className="font-sans font-bold leading-none" style={{
              color: "var(--primary)",
              fontSize: "clamp(3rem, 6vw, 4.5rem)"
            }}>
                  {stat.number}
                </span>
                {stat.unit && <span className="font-sans text-xl md:text-2xl" style={{
              color: "var(--primary)"
            }}>
                    {stat.unit}
                  </span>}
              </div>
              <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground text-center md:text-left">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}